/**
 * Hanlyn Mobile Filter Drawer
 *
 * Bottom drawer for mobile filtering on collection and search pages.
 * Follows the same pattern as hanlyn-quick-shop-drawer.js
 */

if (!customElements.get('hanlyn-filter-drawer')) {
  class HanlynFilterDrawer extends HTMLElement {
    constructor() {
      super();

      this.drawer = null;
      this.overlay = null;
      this.isOpen = false;
      this.lastFocusedElement = null;

      // Touch/swipe support
      this.touchStartY = 0;
      this.touchEndY = 0;
      this.isDragging = false;

      // Bound methods for event listeners
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleApplyClick = this.handleApplyClick.bind(this);
      this.handleEscKey = this.handleEscKey.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleFilterChange = this.handleFilterChange.bind(this);
    }

    connectedCallback() {
      this.drawer = this.querySelector('.hanlyn-filter-drawer__drawer');
      this.overlay = this.querySelector('.hanlyn-filter-drawer__overlay');

      if (!this.drawer) {
        console.error('HanlynFilterDrawer: .hanlyn-filter-drawer__drawer element not found');
        return;
      }

      this.setupEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();

      if (this.isOpen) {
        this.unlockScroll();
      }
    }

    setupEventListeners() {
      // Close button
      const closeButton = this.drawer.querySelector('.hanlyn-filter-drawer__close');
      if (closeButton) {
        closeButton.addEventListener('click', this.handleCloseClick);
      }

      // Apply button
      const applyButton = this.drawer.querySelector('.hanlyn-filter-drawer__apply');
      if (applyButton) {
        applyButton.addEventListener('click', this.handleApplyClick);
      }

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', this.handleOverlayClick);
      }

      // Touch events for swipe-to-close
      if (this.drawer) {
        this.drawer.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.drawer.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        this.drawer.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      }

      // Filter changes - auto-submit form to update results
      const form = this.drawer.querySelector('form');
      if (form) {
        form.addEventListener('change', (event) => {
          // Check if the change came from a filter checkbox
          if (event.target.type === 'checkbox') {
            this.handleFilterChange(event);
          }
        });
      }
    }

    removeEventListeners() {
      const closeButton = this.drawer?.querySelector('.hanlyn-filter-drawer__close');
      if (closeButton) {
        closeButton.removeEventListener('click', this.handleCloseClick);
      }

      const applyButton = this.drawer?.querySelector('.hanlyn-filter-drawer__apply');
      if (applyButton) {
        applyButton.removeEventListener('click', this.handleApplyClick);
      }

      if (this.overlay) {
        this.overlay.removeEventListener('click', this.handleOverlayClick);
      }

      if (this.drawer) {
        this.drawer.removeEventListener('touchstart', this.handleTouchStart);
        this.drawer.removeEventListener('touchmove', this.handleTouchMove);
        this.drawer.removeEventListener('touchend', this.handleTouchEnd);
      }

      document.removeEventListener('keydown', this.handleEscKey);
    }

    openDrawer() {
      if (this.isOpen) return;

      this.lastFocusedElement = document.activeElement;
      this.isOpen = true;

      this.lockScroll();
      this.drawer.classList.add('hanlyn-filter-drawer__drawer--open');
      this.overlay.classList.add('hanlyn-filter-drawer__overlay--visible');

      document.addEventListener('keydown', this.handleEscKey);

      // Update clear filters button state on open
      this.updateClearFiltersState();

      // Focus the drawer
      setTimeout(() => {
        const firstInput = this.drawer.querySelector('input:not([type="hidden"])');
        if (firstInput) {
          firstInput.focus();
        }
      }, 300);
    }

    closeDrawer() {
      if (!this.isOpen) return;

      this.isOpen = false;

      this.drawer.classList.remove('hanlyn-filter-drawer__drawer--open');
      this.overlay.classList.remove('hanlyn-filter-drawer__overlay--visible');

      this.unlockScroll();
      document.removeEventListener('keydown', this.handleEscKey);

      // Return focus
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }
    }

    handleCloseClick(event) {
      event.preventDefault();
      this.closeDrawer();
    }

    handleApplyClick(event) {
      event.preventDefault();
      this.closeDrawer();
    }

    handleOverlayClick(event) {
      event.preventDefault();
      this.closeDrawer();
    }

    handleEscKey(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.closeDrawer();
      }
    }

    handleTouchStart(event) {
      const touch = event.touches[0];
      this.touchStartY = touch.clientY;
      this.isDragging = false;
    }

    handleTouchMove(event) {
      if (!this.isOpen) return;

      const touch = event.touches[0];
      const deltaY = touch.clientY - this.touchStartY;

      // Only allow dragging down
      if (deltaY > 0) {
        this.isDragging = true;

        // Translate drawer down as user drags
        this.drawer.style.transform = `translateY(${deltaY}px)`;

        // Fade out overlay
        const opacity = Math.max(0, 1 - (deltaY / 400));
        this.overlay.style.opacity = opacity;
      }
    }

    handleTouchEnd(event) {
      if (!this.isDragging) return;

      const touch = event.changedTouches[0];
      const deltaY = touch.clientY - this.touchStartY;

      // If dragged down more than 150px, close the drawer
      if (deltaY > 150) {
        this.closeDrawer();
      } else {
        // Snap back to position
        this.drawer.style.transform = '';
        this.overlay.style.opacity = '';
      }

      this.isDragging = false;
    }

    handleFilterChange(event) {
      // Trigger the form submission to update results
      // Dawn's facets.js will intercept this and handle it via AJAX
      const form = event.target.closest('form');
      if (form) {
        // Dispatch a custom event that facets.js listens for
        const changeEvent = new Event('input', { bubbles: true });
        form.dispatchEvent(changeEvent);

        // Also trigger the form's onSubmit if facets system expects it
        // This ensures Dawn's faceting system picks up the change
        if (form.querySelector('facet-filters-form')) {
          const facetForm = form.closest('facet-filters-form');
          if (facetForm && typeof facetForm.onActiveFilterClick === 'function') {
            // Let facets.js handle the update
            return;
          }
        }

        // Fallback: manually trigger form submission via AJAX
        this.submitFilters(form);
      }
    }

    submitFilters(form) {
      // Create FormData from the form
      const formData = new FormData(form);
      const searchParams = new URLSearchParams(formData);

      // Get the current URL and update with new filters
      const url = new URL(window.location.href);

      // Clear existing filter params and add new ones
      Array.from(searchParams.entries()).forEach(([key, value]) => {
        if (key !== 'q' && key !== 'options[prefix]') {
          url.searchParams.delete(key);
        }
      });

      searchParams.forEach((value, key) => {
        url.searchParams.append(key, value);
      });

      // Trigger a page update via fetch (same as facets.js does)
      fetch(url.toString(), {
        headers: {
          'Accept': 'text/html'
        }
      })
      .then(response => response.text())
      .then(html => {
        // Parse the response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Update the product grid
        const newProductGrid = doc.querySelector('#product-grid');
        const currentProductGrid = document.querySelector('#product-grid');

        if (newProductGrid && currentProductGrid) {
          currentProductGrid.innerHTML = newProductGrid.innerHTML;
        }

        // Update the desktop filters if they exist
        const newFilters = doc.querySelector('#FacetFiltersForm');
        const currentFilters = document.querySelector('#FacetFiltersForm');

        if (newFilters && currentFilters) {
          currentFilters.innerHTML = newFilters.innerHTML;
        }

        // Update the mobile filters (this drawer)
        const newMobileFilters = doc.querySelector('#FacetFiltersFormMobile');
        const currentMobileFilters = document.querySelector('#FacetFiltersFormMobile');

        if (newMobileFilters && currentMobileFilters) {
          currentMobileFilters.innerHTML = newMobileFilters.innerHTML;
        }

        // Update URL without reload
        window.history.pushState({}, '', url.toString());

        // Update result count in drawer
        this.updateResultCount(doc);

        // Update clear filters button state
        this.updateClearFiltersState();
      })
      .catch(error => {
        console.error('Filter update failed:', error);
      });
    }

    updateResultCount(doc) {
      // Update the result count on the apply button
      if (!doc) return;

      const resultsCountEl = this.drawer.querySelector('[data-results-count]');
      if (!resultsCountEl) return;

      // Try to find the product count in the new document
      const productCount = doc.querySelector('[data-results-count]');
      if (productCount) {
        resultsCountEl.textContent = productCount.textContent;
      }
    }

    updateClearFiltersState() {
      // Check if any filters are currently active
      const form = this.drawer.querySelector('form');
      if (!form) return;

      const checkedFilters = form.querySelectorAll('input[type="checkbox"]:checked');
      const hasActiveFilters = checkedFilters.length > 0;
      const activeFilterCount = checkedFilters.length;

      // Update mobile drawer clear button
      const mobileClearButton = this.drawer.querySelector('.hanlyn-filter-drawer__clear-link');
      if (mobileClearButton) {
        if (hasActiveFilters) {
          mobileClearButton.classList.remove('hanlyn-filter-drawer__clear-link--disabled');
        } else {
          mobileClearButton.classList.add('hanlyn-filter-drawer__clear-link--disabled');
        }
      }

      // Update desktop sidebar clear button (collection page)
      const desktopClearButton = document.querySelector('.hanlyn-collection__clear-filters');
      if (desktopClearButton) {
        if (hasActiveFilters) {
          desktopClearButton.classList.remove('hanlyn-collection__clear-filters--disabled');
        } else {
          desktopClearButton.classList.add('hanlyn-collection__clear-filters--disabled');
        }
      }

      // Update desktop sidebar clear button (search page)
      const searchClearButton = document.querySelector('.hanlyn-search__clear-filters');
      if (searchClearButton) {
        if (hasActiveFilters) {
          searchClearButton.classList.remove('hanlyn-search__clear-filters--disabled');
        } else {
          searchClearButton.classList.add('hanlyn-search__clear-filters--disabled');
        }
      }

      // Update filter count badge on mobile filter button (collection page)
      const collectionFilterCount = document.querySelector('.hanlyn-collection__filter-count');
      if (collectionFilterCount) {
        if (activeFilterCount > 0) {
          collectionFilterCount.textContent = activeFilterCount;
          collectionFilterCount.style.display = '';
        } else {
          collectionFilterCount.style.display = 'none';
        }
      }

      // Update filter count badge on mobile filter button (search page)
      const searchFilterCount = document.querySelector('.hanlyn-search__filter-count');
      if (searchFilterCount) {
        if (activeFilterCount > 0) {
          searchFilterCount.textContent = activeFilterCount;
          searchFilterCount.style.display = '';
        } else {
          searchFilterCount.style.display = 'none';
        }
      }
    }

    lockScroll() {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    }

    unlockScroll() {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
  }

  customElements.define('hanlyn-filter-drawer', HanlynFilterDrawer);
}
